import React, { useState, useRef, useEffect } from 'react';
import {
  UserDetailHeadingCover,
  InputContainer,
  InputLabel,
  Nav,
  NavLinks,
  NavLink,
  NavLight,
  Wrapper,
  StyledCheckboxWrapper,
  StyledCheckbox,
  IconWrapper,
  LinkContainer,
  LinkWrapper,
  ConfigLink,
  TableTitle,
  SubTitle,
} from './styles';

import Loader from '../../components/Loader';
import { LoginContainer, ModalBody, ModalContainer } from '../../components/Modals/styles';
import { withTranslation } from 'react-i18next';
import { SINGLE_SIGN_ON_SETTINGS as trans } from './TranslationValues';
import { Switch, Input, Icon, Modal, message, Table } from 'antd';
import {
  getDirectorySyncInfo,
  getSsoConfigDetails,
  getSsoConnnectionDetails,
  patchEnableSSO,
  syncDirectory,
  updateSsoConfig,
} from '../../utils/services/ssoAPI';
import SAMLPanel from './SAMLPanel';
import OIDCPanel from './OIDCPanel';

const NAMESPACE = ['SINGLE_SIGN_ON_SETTINGS'];

const INITIAL_SAML_CONFIG = {
  firstName: 'firstName',
  lastName: 'lastName',
  roles: 'docstudio-roles',
  'session-ttl': 'session-ttl',
  ACSUrl: '',
  redirectUrl: '',
  entityID: '',
  endpoint: '',
  secret: '',
};

const mapProviderName = {
  'okta.com': 'okta-scim-v2',
  'sts.windows.net': 'azure-scim-v2',
  'onelogin.com': 'onelogin-scim-v2',
  'jumpcloud.com': 'jumpcloud-scim-v2',
  'google.com': 'google',
};
const SingleSignOnSettings = props => {
  const { t } = props;
  const [loading, setLoading] = useState(false);
  const [flag, setFlag] = useState(false);
  const [enableSSO, setEnableSSO] = useState(false);
  const [directoryEnabled, setDirectoryEnabled] = useState(null);
  const [configModal, setConfigModal] = useState(null);
  const spanRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightPosition, setLightPosition] = useState(27);
  const [lightWidth, setLightWidth] = useState(55.5); // Initial light width
  const [defaultSamlConfig, setDefaultSamlConfig] = useState(INITIAL_SAML_CONFIG);
  const [samlConfig, setSamlConfig] = useState(null);

  const updateLight = index => {
    const spanElement = spanRefs.current[index];
    if (spanElement && typeof spanElement.getBoundingClientRect === 'function') {
      const { left, width } = spanElement.getBoundingClientRect();
      const parentLeft = spanElement.offsetParent.getBoundingClientRect().left;
      setLightPosition(left - parentLeft - 5);
      setLightWidth(width + 12); // Add extra width as needed
    } else {
      console.error('Invalid span element or getBoundingClientRect is not available');
    }
  };

  const handleNavClick = index => {
    setActiveIndex(index);
    updateLight(index);
  };

  const handleCopy = async keyName => {
    try {
      const copyText = defaultSamlConfig[keyName];
      if (copyText) {
        await navigator.clipboard.writeText(copyText);
        message.success('Copied!');
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const columns = [
    {
      title: 'Claim Name',
      dataIndex: 'claimName',
      key: 'claimName',
      width: '20%',
      render: text => (
        <span>
          {defaultSamlConfig[text]}{' '}
          <Icon type={'copy'} onClick={() => handleCopy(text)} style={{ fontSize: '14px' }} />
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'value',
      key: 'value',
    },
  ];

  const data = [
    {
      key: '1',
      claimName: 'firstName',
      value: 'The firstName attribute is required. Please configure it in your IdP.',
    },
    {
      key: '2',
      claimName: 'lastName',
      value: 'The lastName attribute is optional',
    },
    {
      key: '3',
      claimName: 'roles',
      value: "The roles attribute is required. Please configure it in your IdP. (Eg. admin, publisher, designer)",
    },
  ];

  if (configModal === 'oidc') {
    data.push({
      key: '4',
      claimName: 'session-ttl',
      value: 'The session-ttl attribute is optional. It is used to configure session timeout duration. Please configure it in your IdP if required.',
    });
  }

  const handleSave = async (method, payload, callBack) => {
    try {
      const res = await updateSsoConfig(method, payload);
      if (res.statusCode === 1601 && res.data) {
        message.success('Successfully Configured SSO');
        setDefaultSamlConfig(prev => ({ ...prev, ...res.data.configDetails }));
        setEnableSSO(res.data.configDetails.ssoEnabled);
        if (res.data.ssoConnection) {
          let config = res.data.ssoConnection;
          if (config?.oidcProvider) {
            config = {
              ...config,
              oidcClientId: config.oidcProvider.clientId,
              oidcClientSecret: config.oidcProvider.clientSecret,
              discoveryUrl: config?.oidcProvider.discoveryUrl,
              provider: config?.oidcProvider.provider,
              oidcTenant: config?.tenant,
              tenant: undefined
            };
          }
          setSamlConfig(config);
        }
        callBack();
        if(method === 'put'){
          const directorySyncInfo = await getDirectorySyncInfo();
          if (directorySyncInfo.statusCode === 1609) {
            setDefaultSamlConfig(prev => ({ ...prev, endpoint: '', secret: '', }));
            setDirectoryEnabled(null);
          }
        }
      } else {
        message.error('Failed to save or configure sso');
      }
    } catch (error) {
      console.error('Error saving sso config', error);
    }
  };

  const enableSSOwithDelay = (checked) =>{
    if(checked){
      setTimeout(() => {
        setEnableSSO(true);
      }, 1500);
    }else {
      setEnableSSO(false);
    }
  }

  const handleEnableSSO = async checked => {  
    const isSsoDataValid =
      (samlConfig?.tenant || samlConfig?.oidcTenant) &&
      (samlConfig.idpMetadata ||
        (samlConfig.oidcClientId && samlConfig.oidcClientSecret && samlConfig.provider));
    if (isSsoDataValid) {
      setFlag(checked);
      enableSSOwithDelay(checked);
      try {
        const res = await patchEnableSSO(checked);
        if (res.statusCode === 1605){
          setDefaultSamlConfig(prev => ({ ...prev, ssoEnabled: checked }));
          if(samlConfig?.oidcProvider) {
            setTimeout(() => handleNavClick(1), 1500);
          } else {
            handleNavClick(0);
          }
        }
      } catch (error) {
        console.error('Error enabling sso', error);
      }
    } else {
      setFlag(checked);
      enableSSOwithDelay(checked);
    }
  };

  const handleEnableDirectory = async checked =>{
    
      if (checked && (samlConfig.idpMetadata || (samlConfig.oidcClientId && samlConfig.oidcClientSecret && samlConfig.discoveryUrl))) {
      const provider = mapProviderName[samlConfig?.idpMetadata?.provider || samlConfig?.provider];
      if(provider){
        try {
          const res = await syncDirectory({type: provider});
          console.log(res);
          if (res.statusCode === 1606){
            message.success('Directory sync enabled successfully'); 
            setDefaultSamlConfig(prev => ({ ...prev, endpoint: res.data.scim.endpoint, secret: res.data.scim.secret, }));
            setDirectoryEnabled(res.data.id);
            setConfigModal('directory');
          }
        } catch (error) {
          console.error('Error enabling directoty sync', error);
        }
      }
    }
  }

  useEffect(() => {
    // Set initial light position and width
    const spanElement = spanRefs.current[activeIndex];
    if (spanElement && typeof spanElement.getBoundingClientRect === 'function') {
      const { left, width } = spanElement.getBoundingClientRect();
      const parentLeft = spanElement.offsetParent.getBoundingClientRect().left;
      setLightPosition(left - parentLeft - 5);

      setLightWidth(width + 12); // Add 10px as required
    }
  }, [activeIndex]);

  const fetchConfigDetails = async () => {
    try {
      setLoading(true);
      const [configDetails, connectionDetails, directorySyncInfo] = await Promise.all([
        getSsoConfigDetails(),
        getSsoConnnectionDetails(),
        getDirectorySyncInfo()
      ]);

      console.log({configDetails, connectionDetails, directorySyncInfo});

      if (configDetails.statusCode === 1603) {
        setDefaultSamlConfig(prev => ({ ...prev, ...configDetails.data }));
        setEnableSSO(configDetails.data.ssoEnabled);
        setDirectoryEnabled(configDetails.data.directoryId);
        setFlag(configDetails.data.ssoEnabled);
      }

      if (connectionDetails.statusCode === 1603 && connectionDetails.data?.length) {
        let config = connectionDetails.data[0];
        if (config?.oidcProvider) {
          config = {
            ...config,
            oidcClientId: config.oidcProvider.clientId,
            oidcClientSecret: config.oidcProvider.clientSecret,
            discoveryUrl: config?.oidcProvider.discoveryUrl,
            provider: config?.oidcProvider.provider,
            oidcTenant: config?.tenant,
            tenant: undefined
          };
          requestAnimationFrame(() => handleNavClick(1));
        }
        setSamlConfig(config);
      }
      
      if (directorySyncInfo.statusCode === 1608 && directorySyncInfo.data) {
        setDefaultSamlConfig(prev => ({ ...prev, ...directorySyncInfo.data.scim }));
        setDirectoryEnabled(directorySyncInfo.data.id);
      }
    } catch (error) {
      console.error('Error fetching SSO details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigDetails();
  }, []);

  const currentSavedConfig = samlConfig?.oidcProvider ? 'oidc' : samlConfig?.idpMetadata ? 'saml' : '';

  return (
    <>
      {loading ? (
        <LoginContainer>
          <Loader />
        </LoginContainer>
      ) : (
        <>
          <UserDetailHeadingCover>
            <div>
              <h5>{t(trans.SSO_SETTINGS_TITLE.key, trans.SSO_SETTINGS_TITLE.value)}</h5>
            </div>

            {enableSSO && defaultSamlConfig.ssoEnabled && (
              <Switch
                className="sso-switch"
                checkedChildren="SSO Enabled"
                unCheckedChildren="SSO Disabled"
                checked={enableSSO}
                onChange={checked => handleEnableSSO(checked)}
              />
            )}
          </UserDetailHeadingCover>

          {!enableSSO && (
            <Wrapper enableSSO={flag}>
              <StyledCheckboxWrapper>
                <StyledCheckbox
                  type="checkbox"
                  checked={flag}
                  onChange={() => handleEnableSSO(true)}
                />
                <IconWrapper checked={flag} onClick={() => handleEnableSSO(true)}>
                  <Icon type="poweroff" />
                </IconWrapper>
              </StyledCheckboxWrapper>
              <p>Click the above button to enable and manage your Single Sign On</p>
              <LinkContainer>
                Share the below Configuration information with Your Identity Provider (IdP)
                <LinkWrapper>
                  <ConfigLink onClick={() => setConfigModal('saml')}>SAML Configuration</ConfigLink>
                  <span style={{ color: '#999', margin: '0 8px' }}>|</span>
                  <ConfigLink onClick={() => setConfigModal('oidc')}>OIDC Configuration</ConfigLink>
                </LinkWrapper>
              </LinkContainer>
            </Wrapper>
          )}

          {enableSSO && flag && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '90%',
              }}
            >
              <Nav>
                <NavLinks>
                  {['SAML', 'OIDC'].map((icon, index) => (
                    <NavLink key={index} active={index === activeIndex} color={'#FFEB3B'}>
                      <span
                        ref={el => (spanRefs.current[index] = el)}
                        onClick={e => handleNavClick(index, e)}
                      >
                        {icon}
                      </span>
                    </NavLink>
                  ))}
                </NavLinks>
                <NavLight
                  position={lightPosition}
                  width={lightWidth}
                  beforeWidth={lightWidth + 30}
                  color={'#FFEB3B'}
                />
              </Nav>
              {activeIndex === 0 && (
                <SAMLPanel
                  t={t}
                  isVisible={activeIndex === 0}
                  onSave={handleSave}
                  samlConfig={samlConfig}
                  setConfigModal={setConfigModal}
                  directoryEnabled={directoryEnabled}
                  handleEnableDirectory={handleEnableDirectory}
                  currentSavedConfig={currentSavedConfig}
                />
              )}
              {activeIndex === 1 && (
                <OIDCPanel
                  t={t}
                  isVisible={activeIndex === 1}
                  onSave={handleSave}
                  oidcConfig={samlConfig}
                  setConfigModal={setConfigModal}
                  directoryEnabled={directoryEnabled}
                  handleEnableDirectory={handleEnableDirectory}
                  currentSavedConfig={currentSavedConfig}
                />
              )}
            </div>
          )}
        </>
      )}

      <Modal
        title={
          configModal === 'saml' 
            ? 'Service Provider (SP) SAML Configuration' 
            : configModal === 'oidc'
            ? 'Service Provider (SP) OIDC Configuration'
            : 'Service Provider (SP) Directory Sync Configuration'
        }
        centered
        maskClosable={true}
        visible={configModal || false}
        width="850px"
        onCancel={() => setConfigModal(null)}
        footer={false}
        className="saml-modal"
      >
        <ModalBody>
          <ModalContainer width={'100%'}>
            <p style={{ fontSize: '16px', textAlign: 'center', marginBottom: '20px', color:'blue' }}>
              {configModal === 'saml' 
                ? 'Your Identity Provider (IdP) will ask for the following information while configuring the SAML application.' 
                : configModal === 'oidc'
                ? 'Your Identity Provider (IdP) will ask for the following information while configuring the OIDC application.'
                : 'Your Identity Provider (IdP) will ask for the following information while configuring the Directory sync.'}
            </p>
            {configModal === 'saml' ? 
              <>
                <div style={{ paddingTop: '10px' }}>
                  <InputContainer full>
                    <InputLabel>
                      Assertion Consumer Service(ACS) URL / Single Sign-On URL / Destination URL
                    </InputLabel>
                    <Input
                      name="acsUrl"
                      size="large"
                      value={defaultSamlConfig?.ACSUrl || ''}
                      readOnly={true}
                      className="ssoDefaultInput"
                      addonAfter={<Icon type={'copy'} onClick={() => handleCopy('ACSUrl')} />}
                    />
                  </InputContainer>
                  <InputContainer full>
                    <InputLabel>
                      Entity ID / Identifier / Audience URI / Audience Restriction
                    </InputLabel>
                    <Input
                      name="entityId"
                      size="large"
                      className="ssoDefaultInput"
                      readOnly={true}
                      value={defaultSamlConfig?.entityID || ''}
                      addonAfter={<Icon type={'copy'} onClick={() => handleCopy('entityID')} />}
                    />
                  </InputContainer>
                </div>
                <div style={{ marginTop: '35px' }}>
                  <Table
                    bordered
                    title={() => (
                      <TableTitle>
                        Attributes & Claims
                        <SubTitle>
                          Under the Attributes & Claims section in your IdP, you have to configure the
                          following claim names
                        </SubTitle>
                      </TableTitle>
                    )}
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                  />
                </div>
              </>
              : configModal === 'oidc' ?
              <>
                <div style={{ paddingTop: '10px' }}>
                  <InputContainer full>
                    <InputLabel>
                      Redirect URI / Callback URL
                    </InputLabel>
                    <Input
                      name="redirectUri"
                      size="large"
                      value={defaultSamlConfig?.redirectUrl || ''}
                      readOnly={true}
                      className="ssoDefaultInput"
                      addonAfter={<Icon type={'copy'} onClick={() => handleCopy('redirectUrl')} />}
                    />
                  </InputContainer>
                </div>
                <div style={{ marginTop: '35px' }}>
                  <Table
                    bordered
                    title={() => (
                      <TableTitle>
                        Attributes & Claims
                        <SubTitle>
                          Under the Attributes & Claims section in your IdP, you have to configure the
                          following claim names
                        </SubTitle>
                      </TableTitle>
                    )}
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                  />
                </div>
              </>
              : 
              <>
              <div style={{ paddingTop: '10px' }}>
                  <InputContainer full>
                    <InputLabel>
                      SCIM Endpoint
                    </InputLabel>
                    <Input
                      name="endPoint"
                      size="large"
                      value={defaultSamlConfig?.endpoint || ''}
                      readOnly={true}
                      className="ssoDefaultInput"
                      addonAfter={<Icon type={'copy'} onClick={() => handleCopy('endpoint')} />}
                    />
                  </InputContainer>
                  <InputContainer full>
                    <InputLabel>
                      SCIM Token
                    </InputLabel>
                    <Input
                      name="token"
                      size="large"
                      className="ssoDefaultInput"
                      readOnly={true}
                      value={defaultSamlConfig?.secret || ''}
                      addonAfter={<Icon type={'copy'} onClick={() => handleCopy('secret')} />}
                    />
                  </InputContainer>
                </div>
              </>
            }
          </ModalContainer>
        </ModalBody>
      </Modal>
    </>
  );
};
export default withTranslation(NAMESPACE)(SingleSignOnSettings);
